// 스냅샷 동기화 판정(D5a §4, docs/ACCOUNT-SAVE-PRD.md). DOM·fetch 없음: 저장소·전송·시계를 주입받아 시험한다.
// 규칙 요약: 서버 비어 있음 → 올림(첫 이관) · 서버가 base 그대로 → 로컬 바뀌었으면 올림 · 로컬이 base 그대로 → 당겨옴(빨리감기)
//            둘 다 바뀜 → 진행 점수가 크거나 같은 로컬은 올림, 서버가 크면 당겨옴. 당겨오기는 대기 키에 두고 부팅 때 적용(applyStagedRestore).
// 원칙: 이 모듈의 어떤 실패도 던져서 게임을 멈추지 않는다(호출자 AccountClient가 결과만 상태 줄에 쓴다).
import { ACCOUNT_APPLY_KEY, ACCOUNT_LOCAL_BACKUP_KEY, ACCOUNT_META_KEY, COPY_SUFFIXES, SYNC_GEN, SYNC_KEYS, collectSnapshot, syncKeyGen, parseSnapshot, sameSnapshot, snapshotBytes, snapshotHash, writeSnapshot, type KV } from './keys';
import { compareScore, progressScore } from './score';
import { MAX_SNAPSHOT_BYTES, type ConflictResponse, type PutResponse, type Provider, type SaveResponse, type Snapshot } from './protocol';

/**
 * g = 이 기준을 적은 빌드의 진행 키 세대(SYNC_GEN). 없으면 0(세대 도입 전 빌드가 적은 기준).
 * 옛 빌드가 적은 기준 위에서는 서버가 이어 둔 새 세대 키를 로컬이 아직 모른다 → 먼저 맞춘다(reconcileGen, PR #260 Codex P1)
 */
export interface Base { accountId: string; rev: number; hash: string; g?: number }
export interface Meta {
  v: 1;
  /** 로컬이 마지막으로 서버와 같았던 rev와 그때 스냅샷 해시 */
  base: Base | null;
  accountId: string | null;
  provider: Provider | null;
  lastSyncAt: number;
  /** 계정 삭제 뒤 로컬 유지를 고르면 켜진다: 새 게스트를 자동으로 만들지 않는다 */
  optOut: boolean;
  /** 마지막으로 서버 진행을 적용한 시각(새로고침 반복 방지) */
  lastApplyAt: number;
  /** 적용 실패 뒤 당겨오기를 쉬는 시각(이 시각 전에는 당겨오지 않는다) */
  pullBlockedUntil: number;
  /** 다음 부팅 때 보일 "다른 기기의 진행을 불러왔어요" 알림 */
  restoredNotice: boolean;
  /** Google 연결 계정의 세션이 사라졌다(다른 기기에서 삭제·폐기): 새 게스트를 몰래 만들지 않고 다시 연결을 기다린다 */
  needsRelink: boolean;
  lastError: string | null;
}

export function freshMeta(): Meta {
  return { v: 1, base: null, accountId: null, provider: null, lastSyncAt: 0, optOut: false, lastApplyAt: 0, pullBlockedUntil: 0, restoredNotice: false, needsRelink: false, lastError: null };
}

/** 동기화 엔진 자신의 로컬 키(메타·당겨온 스냅샷 대기·적용 전 사본). 계정 동기화는 기본값, 캐릭터 동기화는 캐릭터마다 따로(characters/keys.ts charSyncKeys) */
export interface SyncKeys { meta: string; apply: string; backup: string }
export const ACCOUNT_SYNC_KEYS: SyncKeys = { meta: ACCOUNT_META_KEY, apply: ACCOUNT_APPLY_KEY, backup: ACCOUNT_LOCAL_BACKUP_KEY };

export function loadMeta(storage: KV, key: string = ACCOUNT_META_KEY): Meta {
  try {
    const raw = storage.getItem(key);
    if (!raw) return freshMeta();
    const p = JSON.parse(raw) as Partial<Meta>;
    if (!p || p.v !== 1) return freshMeta();
    const m = { ...freshMeta(), ...p };
    const b = m.base;
    if (b && (typeof b.accountId !== 'string' || !Number.isSafeInteger(b.rev) || typeof b.hash !== 'string')) m.base = null;
    return m;
  } catch { return freshMeta(); }
}
export function saveMeta(storage: KV, m: Meta, key: string = ACCOUNT_META_KEY): void {
  try { storage.setItem(key, JSON.stringify(m)); } catch { /* 메타 저장 실패는 다음 동기화가 다시 판정한다 */ }
}

/** 전송 계층 오류(HTTP 상태·코드). status 0 = 네트워크 */
export class TransportError extends Error {
  constructor(readonly status: number, readonly code: string, readonly retryAfterMs?: number) { super(code); }
}
export interface Transport {
  /** metaOnly면 rev·점수만(원문 없음) */
  getSave(metaOnly?: boolean): Promise<SaveResponse>;
  /** 200이면 ok, 409면 conflict(최신 메타). 그 밖은 TransportError. overwrite = 충돌 판정으로 다른 기기 진행을 덮어씀(서버가 항상 보관) */
  putSave(rev: number, data: Snapshot, overwrite?: boolean): Promise<{ ok: true; res: PutResponse } | { ok: false; conflict: ConflictResponse }>;
}

export type SyncOutcome =
  | { kind: 'noop' }
  | { kind: 'pushed'; rev: number; clamped: string[] }
  /** 서버 진행을 대기 키에 두었다: 호출자가 저장을 비우고 새로고침해야 적용된다 */
  | { kind: 'staged'; rev: number }
  /** 당겨와야 하지만 지금은 안전한 장면(타이틀·마을)이 아니다 */
  | { kind: 'deferred' }
  /** 당겨오기를 쉬는 중(직전 적용 실패·반복 방지) */
  | { kind: 'blocked' }
  | { kind: 'too-large'; bytes: number }
  /** 로컬 저장소를 읽을 수 없다(빈 진행으로 착각해 서버를 덮지 않는다) */
  | { kind: 'unavailable' };

/** 같은 계정에서 새로고침 반복을 막는 최소 간격 */
export const APPLY_COOLDOWN_MS = 60_000;
/** 적용 실패 뒤 당겨오기를 쉬는 시간 */
export const PULL_BLOCK_MS = 3_600_000;

export interface SyncOptions {
  accountId: string;
  /** 지금 당겨와도 되는 장면인가(타이틀·마을) */
  canPull: boolean;
  /** base가 있어도 서버를 확인한다(원격 변경 확인) */
  check: boolean;
}

export class SyncEngine {
  meta: Meta;
  /**
   * pullFirst(캐릭터 동기화): 이 계정에서 맞춘 기준(base)이 없는데 서버에 진행이 있으면 점수와 상관없이 먼저 받는다(push 거절) —
   * 처음 보는 캐릭터의 기본값이 서버 진행을 덮지 않게(Codex P2 #214)
   */
  constructor(private readonly storage: KV, private readonly transport: Transport, private readonly now: () => number = () => Date.now(), readonly keys: SyncKeys = ACCOUNT_SYNC_KEYS, private readonly pullFirst = false) {
    this.meta = loadMeta(storage, keys.meta);
  }
  /** 이 엔진의 메타를 저장한다 */
  persist(): void { saveMeta(this.storage, this.meta, this.keys.meta); }

  /** 로컬이 마지막 동기화 이후 바뀌었는지(계정이 같을 때). 판단할 수 없으면 true */
  localChanged(accountId: string): boolean {
    const local = collectSnapshot(this.storage);
    const b = this.baseFor(accountId);
    return !local || !b || snapshotHash(local) !== b.hash;
  }
  /**
   * 이 계정에서 쓸 기준. 계정 동기화는 계정 id가 같아야 하고, 캐릭터 동기화(pullFirst)는 캐릭터 id(메타 키) + 서버 rev로 판정한다 —
   * 게스트 병합처럼 계정 id만 바뀌어도(서버가 같은 rev로 옮김) "처음 보는 캐릭터"로 보지 않는다(Codex P1 #214). rev가 다르면 서버 판정으로 간다
   */
  private baseFor(accountId: string): Base | null {
    const b = this.meta.base;
    if (!b) return null;
    return this.pullFirst || b.accountId === accountId ? b : null;
  }

  private setBase(accountId: string, rev: number, hash: string): void {
    this.meta.base = { accountId, rev, hash, g: SYNC_GEN };
    this.meta.lastSyncAt = this.now();
    this.meta.lastError = null;
    this.persist();
  }

  /**
   * 세대 맞추기(PR #260 Codex P1×2·P2): 기준이 옛 세대(g < SYNC_GEN)면 서버 원문을 한 번 받아, 기준 세대가 몰랐던 키 중
   * 서버에만 있는 것(옛 빌드가 올릴 때 서버가 이어 둔 환생·필드 진행)을 찾는다. 그러지 않으면 업그레이드한 기기가 그 키 없이
   * g=SYNC_GEN으로 올려 서버 진행을 지운다. 로컬 저장소에 직접 쓰지 않는다 — 실행 중인 게임 저장 모듈이 옛 값을 들고 있다가
   * 덮어쓰므로 늘 대기 키 + 새로고침 적용(stage)으로 넣는다:
   *  - 서버에만 있는 키 없음 → 기준 세대만 올리고 평소대로 진행(null)
   *  - 로컬이 기준 그대로 → 서버 원문을 그대로 당겨옴(빨리감기 — 서버 키를 로컬 변경으로 세지 않는다)
   *  - 로컬이 바뀜 → 로컬 + 서버에만 있는 키를 적용하되 기준 해시는 옛 기준 그대로(적용 뒤에도 '로컬 변경'으로 남아 다음 동기화가 올린다)
   */
  private async reconcileGen(o: SyncOptions, base: Base, local: Snapshot, hash: string): Promise<SyncOutcome | null> {
    const bg = base.g ?? 0;
    const server = await this.transport.getSave(false);
    const remote = server.rev > 0 && server.data ? parseSnapshot(server.data, true) : null;
    const missing: string[] = [];
    if (remote) for (const k of SYNC_KEYS) if (syncKeyGen(k) > bg && Object.hasOwn(remote.keys, k) && !Object.hasOwn(local.keys, k)) missing.push(k);
    if (missing.length === 0) {
      this.meta.base = { ...base, g: SYNC_GEN };
      this.persist();
      return null;
    }
    if (hash === base.hash) return this.stage(o, server.rev, remote!, snapshotHash(remote!));
    const merged: Snapshot = { v: 1, keys: {}, g: SYNC_GEN };
    for (const k of SYNC_KEYS) {
      if (Object.hasOwn(local.keys, k)) merged.keys[k] = local.keys[k];
      else if (missing.includes(k)) merged.keys[k] = remote!.keys[k];
    }
    return this.stage(o, base.rev, merged, snapshotHash(merged), base.hash);
  }

  async sync(o: SyncOptions): Promise<SyncOutcome> {
    const local = collectSnapshot(this.storage);
    if (!local) return { kind: 'unavailable' };
    const bytes = snapshotBytes(local);
    const hash = snapshotHash(local);
    const pre = this.baseFor(o.accountId);
    if (pre && (pre.g ?? 0) < SYNC_GEN) {
      let r: SyncOutcome | null;
      try { r = await this.reconcileGen(o, pre, local, hash); } catch { return { kind: 'noop' }; } // 맞추지 못하면 이번엔 올리지 않는다(다음 주기에 다시)
      if (r) return r;
    }
    const base = this.baseFor(o.accountId);
    if (base && !o.check) {
      if (hash === base.hash) return { kind: 'noop' };
      if (bytes > MAX_SNAPSHOT_BYTES) return { kind: 'too-large', bytes };
      const r = await this.transport.putSave(base.rev, local, false);
      if (r.ok) { this.setBase(o.accountId, r.res.rev, hash); return { kind: 'pushed', rev: r.res.rev, clamped: r.res.clamped }; }
      // 다른 기기가 먼저 썼다 → 서버를 읽고 판정
    }
    for (let attempt = 0; attempt < 3; attempt++) {
      // 원격 변경 확인은 메타(rev)부터: 서버가 base 그대로면 원문을 내려받지 않는다
      if (base) {
        const meta = await this.transport.getSave(true);
        if (meta.rev === base.rev) {
          if (hash === base.hash) { this.setBase(o.accountId, base.rev, hash); return { kind: 'noop' }; }
          const out = await this.push(o, base.rev, local, hash, bytes, false);
          if (out !== 'retry') return out;
          continue;
        }
      }
      const server = await this.transport.getSave(false);
      const out = await this.decide(o, local, hash, bytes, base, server);
      if (out !== 'retry') return out;
    }
    return { kind: 'noop' };
  }

  private async push(o: SyncOptions, rev: number, local: Snapshot, hash: string, bytes: number, overwrite: boolean): Promise<SyncOutcome | 'retry'> {
    if (bytes > MAX_SNAPSHOT_BYTES) return { kind: 'too-large', bytes };
    const r = await this.transport.putSave(rev, local, overwrite);
    if (!r.ok) return 'retry';
    this.setBase(o.accountId, r.res.rev, hash);
    return { kind: 'pushed', rev: r.res.rev, clamped: r.res.clamped };
  }

  private async decide(o: SyncOptions, local: Snapshot, hash: string, bytes: number, base: Base | null, server: SaveResponse): Promise<SyncOutcome | 'retry'> {
    // 서버 비어 있음: 지금 로컬 진행을 그대로 올린다(첫 이관 — 키운 사람 보존 1순위)
    if (server.rev === 0 || !server.data) return this.push(o, 0, local, hash, bytes, false);
    if (base && server.rev === base.rev) {
      if (hash === base.hash) { this.setBase(o.accountId, base.rev, hash); return { kind: 'noop' }; }
      return this.push(o, server.rev, local, hash, bytes, false);
    }
    const remote = parseSnapshot(server.data, true);
    if (!remote) return { kind: 'noop' };
    const remoteHash = snapshotHash(remote);
    if (remoteHash === hash) { this.setBase(o.accountId, server.rev, hash); return { kind: 'noop' }; }
    if (this.pullFirst && base === null && Object.keys(remote.keys).length > 0) return this.stage(o, server.rev, remote, remoteHash);
    // 로컬이 비어 있고 서버에 진행이 있으면 늘 당겨온다(빈 값으로 서버 진행을 덮지 않는다 — 점수가 0인 진행 기록도 보존)
    if (Object.keys(local.keys).length === 0 && Object.keys(remote.keys).length > 0) return this.stage(o, server.rev, remote, remoteHash);
    // 로컬이 base 이후 그대로면 다른 기기가 이어서 진행한 것: 빨리감기로 당겨온다(점수와 무관 — 핑퐁 방지)
    const localUnchanged = base !== null && hash === base.hash;
    if (!localUnchanged && compareScore(progressScore(local), progressScore(remote)) >= 0) {
      // 로컬 진행이 더 많거나 같다(같으면 로컬의 비진행 변화를 살린다): 서버 이전 것은 보관함으로
      return this.push(o, server.rev, local, hash, bytes, true);
    }
    return this.stage(o, server.rev, remote, remoteHash);
  }

  /** baseHash: 적용 뒤 기준 해시(없으면 적용한 스냅샷 해시). 세대 맞추기가 '로컬 변경'을 유지하려고 옛 기준 해시를 넘긴다 */
  private stage(o: SyncOptions, rev: number, remote: Snapshot, remoteHash: string, baseHash?: string): SyncOutcome {
    const now = this.now();
    if (now < this.meta.pullBlockedUntil || now - this.meta.lastApplyAt < APPLY_COOLDOWN_MS) return { kind: 'blocked' };
    if (!o.canPull) return { kind: 'deferred' };
    const text = JSON.stringify(baseHash === undefined ? { accountId: o.accountId, rev, hash: remoteHash, snapshot: remote } : { accountId: o.accountId, rev, hash: remoteHash, snapshot: remote, baseHash });
    const applyKey = this.keys.apply;
    try {
      this.storage.setItem(applyKey, text);
      if (this.storage.getItem(applyKey) !== text) throw new Error('verify');
    } catch {
      try { this.storage.removeItem(applyKey); } catch { /* 무시 */ }
      this.meta.lastError = 'stage-failed';
      this.meta.pullBlockedUntil = now + PULL_BLOCK_MS;
      this.persist();
      return { kind: 'blocked' };
    }
    return { kind: 'staged', rev };
  }
}

export type ApplyResult = { applied: false; reason: 'none' | 'invalid' | 'unavailable' | 'backup-failed' | 'write-failed' } | { applied: true; rev: number } | { applied: true; wiped: true };

/** 계정 삭제 + "이 기기의 진행도 지우기": 실행 중인 게임이 새로고침 직전(pagehide) 메모리 진행을 다시 쓰지 못하게 부팅 단계에서 지운다 */
export function stageWipe(storage: KV): boolean {
  const text = JSON.stringify({ wipe: true });
  try { storage.setItem(ACCOUNT_APPLY_KEY, text); return storage.getItem(ACCOUNT_APPLY_KEY) === text; } catch { return false; }
}

/**
 * 부팅 맨 처음(저장 모듈이 키를 읽기 전) 대기 중인 서버 스냅샷을 적용한다(§4.4).
 * 1) 지금 로컬 진행을 사본 키에 보관하고 되읽어 확인 2) 스냅샷 쓰기(없는 진행 키는 지움, .good 맞춤) 3) base 갱신·대기 키 삭제.
 * 사본 보관이 안 되면 적용하지 않는다(로컬을 사본 없이 지우지 않는다). 쓰기 실패면 로컬 원래 값으로 되돌린다.
 */
export function applyStagedRestore(storage: KV, now: () => number = () => Date.now(), keys: SyncKeys = ACCOUNT_SYNC_KEYS): ApplyResult {
  let raw: string | null;
  try { raw = storage.getItem(keys.apply); } catch { return { applied: false, reason: 'unavailable' }; }
  if (raw === null) return { applied: false, reason: 'none' };
  const meta = loadMeta(storage, keys.meta);
  const fail = (reason: 'invalid' | 'backup-failed' | 'write-failed'): ApplyResult => {
    try { storage.removeItem(keys.apply); } catch { /* 무시 */ }
    meta.lastError = 'apply-' + reason;
    if (reason !== 'invalid') meta.pullBlockedUntil = now() + PULL_BLOCK_MS;
    saveMeta(storage, meta, keys.meta);
    return { applied: false, reason };
  };
  let staged: { accountId?: unknown; rev?: unknown; snapshot?: unknown; wipe?: unknown; baseHash?: unknown };
  try { staged = JSON.parse(raw) as typeof staged; } catch { return fail('invalid'); }
  if (staged && staged.wipe === true) {
    // 사용자가 고른 삭제: 진행 키·사본·적용 전 사본까지 지우고 메타를 처음으로(사본을 남기지 않는다). 캐릭터 키는 bootCharacters가 지운다(wiped 결과)
    try {
      for (const k of SYNC_KEYS) for (const suffix of ['', ...COPY_SUFFIXES]) storage.removeItem(k + suffix);
      storage.removeItem(keys.backup);
      storage.removeItem(keys.apply);
    } catch { return { applied: false, reason: 'unavailable' }; }
    saveMeta(storage, freshMeta(), keys.meta);
    return { applied: true, wiped: true };
  }
  const snap = parseSnapshot(staged?.snapshot, true);
  if (!snap || typeof staged.accountId !== 'string' || typeof staged.rev !== 'number' || !Number.isSafeInteger(staged.rev)) return fail('invalid');
  const local = collectSnapshot(storage);
  if (!local) return { applied: false, reason: 'unavailable' };
  if (!sameSnapshot(local, snap)) {
    const backup = JSON.stringify({ at: now(), accountId: meta.accountId, snapshot: local });
    try {
      storage.setItem(keys.backup, backup);
      if (storage.getItem(keys.backup) !== backup) return fail('backup-failed');
    } catch { return fail('backup-failed'); }
    // 같은 계정을 이어 받을 때만 옛 빌드 스냅샷의 '모르는 키'를 로컬에 남긴다(계정 전환·첫 연결은 그대로 덮는다, PR #260 리뷰 P1)
    try { writeSnapshot(storage, snap, meta.accountId !== null && meta.accountId === staged.accountId); }
    catch {
      try { writeSnapshot(storage, local); } catch { /* 사본 키에 남아 있다 */ }
      return fail('write-failed');
    }
  }
  meta.base = { accountId: staged.accountId, rev: staged.rev, hash: typeof staged.baseHash === 'string' ? staged.baseHash : snapshotHash(snap), g: SYNC_GEN };
  meta.accountId = staged.accountId;
  meta.lastApplyAt = now();
  meta.lastSyncAt = now();
  meta.restoredNotice = true;
  meta.lastError = null;
  saveMeta(storage, meta, keys.meta);
  try { storage.removeItem(keys.apply); } catch { /* 다음 부팅에 같은 스냅샷을 다시 적용해도 결과가 같다 */ }
  return { applied: true, rev: staged.rev };
}
